import { Alert, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import { DocumentScanner } from '@/components/DocumentScanner';
import { Icon } from '@/components/Icon/Icon';
import { IconCell } from '@/components/Icon/IconCell';
import { IdmButton } from '@/components/Idmission/IdmButton.styled';
import { IdmCard } from '@/components/Idmission/IdmCard.styled';
import { Form } from '@/components/Layout/Layout.styled';
import { UnifiedPage } from '@/components/Layout/Page/UnifiedPage';
import { BackToLoginButton } from '@/components/Layout/Page/UnifiedPage.styled';

import { ROUTE } from '@/constants/routes';

import { useAppSelector } from '@/hooks/redux';
import { usePreloadOpenCV } from '@/hooks/usePreloadOpenCV';
import { useTranslate } from '@/hooks/useTranslate';
import { useMinorIdentityUpload } from '@/hooks/verification/useMinorIdentityUpload';

import { selectLoggedInUser } from '@/redux/auth/selectors';
import { selectRightStartFeature } from '@/redux/configs/selectors';

import { DocumentType } from '@/types/document';

import { getDataTestId } from '@/utils/browser';
import { PROGRESS_STEPS } from '@/utils/progressSteps';
import { renderHTMLText } from '@/utils/string';
import { isSurinameUser } from '@/utils/user';

type VerifyMinorBirthCertificatePageProps = {
  documentType?: DocumentType.BirthCertificate | DocumentType.FamilyBook;
};

const CONTENT_MAX_WIDTH = 360;

export function VerifyMinorBirthCertificatePage({
  documentType = DocumentType.BirthCertificate,
}: VerifyMinorBirthCertificatePageProps) {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const user = useAppSelector(selectLoggedInUser);
  const enabledRightStartAccount = useAppSelector(selectRightStartFeature);
  const [isCaptureStarted, setIsCaptureStarted] = useState(false);
  const { upload, error, resetError } = useMinorIdentityUpload(documentType);
  const isFamilyBook = documentType === DocumentType.FamilyBook;
  const isSuriname = isSurinameUser(user);

  usePreloadOpenCV();

  const handleBack = useCallback(() => {
    navigate(ROUTE.VERIFY_MINOR_ID, { state: { skipRejection: true } });
  }, [navigate]);

  const handleStartCapture = useCallback(() => {
    resetError();
    setIsCaptureStarted(true);
  }, [resetError]);

  useEffect(() => {
    if (isFamilyBook && !isSuriname) {
      navigate(ROUTE.VERIFY_MINOR_ID, { replace: true });
    } else if (user && !enabledRightStartAccount) {
      navigate(ROUTE.MORE_INFORMATION, { replace: true });
    }
  }, [navigate, user, enabledRightStartAccount, isFamilyBook, isSuriname]);

  if ((user && !enabledRightStartAccount) || (isFamilyBook && !isSuriname)) {
    return null;
  }

  if (isCaptureStarted) {
    return (
      <DocumentScanner
        autoCapture={true}
        onConfirm={upload}
        error={error}
        onBack={() => setIsCaptureStarted(false)}
        onRetake={resetError}
      />
    );
  }

  return (
    <UnifiedPage
      hasDarkBackground
      bgColor={(theme: Theme) => theme.palette.primaries[900]}
      progressProps={
        isFamilyBook
          ? undefined
          : {
              ...PROGRESS_STEPS.livenessVerificationStep,
              activeColor: (theme: Theme) => theme.palette.primaries[400],
              inactiveColor: (theme: Theme) => theme.palette.grey[300],
            }
      }
      navBarContent={
        <BackToLoginButton
          {...getDataTestId(
            isFamilyBook ? 'family-book-back' : 'birth-cert-back',
          )}
          variant="text"
          startIcon={
            <Icon
              name="arrow-left"
              color={(theme: Theme) => theme.palette.common.white}
            />
          }
          onClick={handleBack}
          sx={{ color: 'common.white' }}
        >
          {t('common.button.back')}
        </BackToLoginButton>
      }
      scrollable
    >
      <Typography variant="h1" mt={1}>
        {t('verification.verifyMinorID.title')}
      </Typography>
      <Form
        style={{ gap: 8, maxWidth: CONTENT_MAX_WIDTH, alignSelf: 'flex-start' }}
      >
        <Typography variant="body2" sx={{ '& ul': { mb: 1 } }}>
          {renderHTMLText(
            t(
              isFamilyBook
                ? 'verification.verifyMinorID.familyBook.instruction'
                : 'verification.verifyMinorID.birthCertificate.instruction',
            ),
          )}
        </Typography>
        {isFamilyBook && (
          <Alert
            icon={<Icon name="info" />}
            severity="info"
            variant="outlined"
            sx={{ width: '100%' }}
          >
            {t('verification.verifyMinorID.familyBook.cbbReminder')}
          </Alert>
        )}
      </Form>

      <IdmCard maxWidth={CONTENT_MAX_WIDTH}>
        <IconCell
          name="check"
          color={(theme: Theme) => theme.palette.common.white}
          bgColor={(theme: Theme) => theme.palette.primaries[500]}
          size={80}
          iconSize={40}
        />
        <IdmButton
          {...getDataTestId(
            isFamilyBook
              ? 'family-book-capture-with-camera'
              : 'birth-cert-capture-with-camera',
          )}
          onClick={handleStartCapture}
          variant="contained"
          size="large"
        >
          {t('verification.verifyMinorID.captureWithCamera')}
        </IdmButton>
      </IdmCard>
    </UnifiedPage>
  );
}

export default VerifyMinorBirthCertificatePage;
